<!-- Sidebar Container -->
<div class="sidebar-container" [class.collapsed]="isCollapsed">
  <!-- Logo + collapse toggle -->
  <div class="sidebar-logo">
    <a [href]="logoLink" class="sidebar-logo__link">
      @if (!isCollapsed) {
        <img [src]="logoUrl" alt="Yuzeat" class="sidebar-logo__wordmark" />
      } @else {
        <!-- Icon-only mark for the collapsed rail: reuses the favicon
             apple-touch icon (yellow lemon on dark background). -->
        <img src="assets/favicon/apple-touch-icon.png" alt="Yuzeat" class="sidebar-logo__icon" />
      }
    </a>
    @if (canToggle) {
      <button
        type="button"
        class="sidebar-toggle"
        (click)="toggleCollapsed()"
        [attr.aria-label]="(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse') | translate"
        [title]="(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse') | translate"
        id="sidebar-toggle-button"
      >
        <span nz-icon [nzType]="isCollapsed ? 'menu-unfold' : 'menu-fold'"></span>
      </button>
    }
  </div>

  <!-- Restaurant name — always shown when a restaurant is selected; only
       becomes a switcher when the user has access to 2+ restaurants. -->
  @if (currentRestaurant.current) {
    <div
      class="restaurant-picker"
      [class.open]="restaurantPickerOpen()"
      [class.collapsed]="isCollapsed"
    >
      <button
        type="button"
        class="restaurant-picker__trigger"
        [class.restaurant-picker__trigger--static]="!currentRestaurant.hasMultiple"
        [disabled]="!currentRestaurant.hasMultiple"
        (click)="toggleRestaurantPicker($event)"
        [attr.aria-label]="currentRestaurant.current?.name || ('common.restaurant' | translate)"
        [title]="currentRestaurant.current?.name || ('common.restaurant' | translate)"
        id="sidebar-restaurant-picker-trigger"
      >
        <span class="restaurant-picker__icon">
          <span nz-icon nzType="environment" nzTheme="fill"></span>
        </span>
        @if (!isCollapsed) {
          <span class="restaurant-picker__labels">
            <span class="restaurant-picker__name">
              {{ currentRestaurant.current?.name || ('common.restaurant' | translate) }}
            </span>
            @if (formatRestaurantAddress(currentRestaurant.current); as addr) {
              <span class="restaurant-picker__address">{{ addr }}</span>
            }
          </span>
          @if (currentRestaurant.hasMultiple) {
            <span class="restaurant-picker__chevron">
              <span nz-icon nzType="down"></span>
            </span>
          }
        }
      </button>

      @if (restaurantPickerOpen() && currentRestaurant.hasMultiple) {
        <div class="restaurant-picker__menu" (click)="$event.stopPropagation()">
          @for (r of currentRestaurant.restaurants(); track r._id) {
            <button
              type="button"
              class="restaurant-picker__item"
              [class.active]="r._id === currentRestaurant.currentId()"
              (click)="selectRestaurant(r._id)"
              [attr.id]="'sidebar-restaurant-item-' + r._id"
            >
              <span class="restaurant-picker__icon">
                <span nz-icon nzType="environment" nzTheme="fill"></span>
              </span>
              <span class="restaurant-picker__labels">
                <span class="restaurant-picker__name">{{ r.name }}</span>
                @if (formatRestaurantAddress(r); as addr) {
                  <span class="restaurant-picker__address">{{ addr }}</span>
                }
              </span>
            </button>
          }
        </div>
      }
    </div>
  }

  <!-- Menu Section -->
  <div class="sidebar-menu">
    <ul nz-menu nzTheme="dark" nzMode="inline" [nzInlineCollapsed]="isCollapsed">
      @for (item of visibleMenuItems; track item.routerLink || item.title) {
        <!-- Menu item with children (submenu) -->
        @if (item.children && item.children.length > 0) {
          <ng-template #submenuTitle>
            <span class="submenu-title-content">
              @if (item.iconType === 'svg' && item.iconPath) {
                <span class="custom-icon">
                  <img
                    [src]="item.iconPath"
                    [alt]="item.title | translate"
                    width="18"
                    height="18"
                  />
                </span>
              }
              @if (item.iconType === 'nz-icon' && item.icon) {
                <span nz-icon [nzType]="item.icon"></span>
              }
              <span>{{ item.title | translate }}</span>
            </span>
          </ng-template>
          <li
            nz-submenu
            [nzOpen]="isSubmenuOpen(item)"
            (nzOpenChange)="onSubmenuOpenChange(item, $event)"
            [nzTitle]="submenuTitle"
            [ngClass]="item.cssClass"
            [class.menu-item--locked]="isLocked(item)"
            [attr.id]="
              'sidebar-nav-submenu-' + (item.routerLink || item.title).replaceAll('/', '-')
            "
          >
            <ul>
              @for (child of item.children; track child.routerLink || child.title) {
                <li
                  nz-menu-item
                  [routerLink]="isLocked(child) ? null : child.routerLink"
                  routerLinkActive="ant-menu-item-selected"
                  [routerLinkActiveOptions]="{ exact: false }"
                  [ngClass]="child.cssClass"
                  [class.menu-item--locked]="isLocked(child)"
                  (click)="isLocked(child) && onLockedClick(child, $event)"
                  [attr.id]="
                    'sidebar-nav-item-' + (child.routerLink || child.title).replaceAll('/', '-')
                  "
                >
                  <span class="menu-item-label">{{ child.title | translate }}</span>
                  @if (isLocked(child)) {
                    <span nz-icon nzType="lock" class="menu-item-lock"></span>
                  }
                </li>
              }
            </ul>
          </li>
        }

        <!-- Simple menu item without children -->
        @if (!item.children || item.children.length === 0) {
          <!-- routerLink on the <li> itself: Angular adds the click handler to
               the host, so tapping anywhere on the row navigates (including
               on the icon in collapsed mode). `routerLinkActive` replaces
               the prior `nzMatchRouter` for the highlight, since nz-menu-
               item's @ContentChild(RouterLink) cannot see a directive that
               lives on its own host element. -->
          <li
            nz-menu-item
            [routerLink]="isLocked(item) ? null : item.routerLink"
            routerLinkActive="ant-menu-item-selected"
            [routerLinkActiveOptions]="{ exact: false }"
            [ngClass]="item.cssClass"
            [class.menu-item--locked]="isLocked(item)"
            (click)="isLocked(item) && onLockedClick(item, $event)"
            [attr.id]="'sidebar-nav-item-' + (item.routerLink || item.title).replaceAll('/', '-')"
          >
            @if (item.iconType === 'svg' && item.iconPath) {
              <span class="custom-icon">
                <img [src]="item.iconPath" [alt]="item.title | translate" width="18" height="18" />
              </span>
            }
            @if (item.iconType === 'nz-icon' && item.icon) {
              <span nz-icon [nzType]="item.icon"></span>
            }
            <span class="menu-item-label">{{ item.title | translate }}</span>
            @if (item.badge && item.badge() > 0) {
              <span class="menu-item-badge">{{ item.badge() }}</span>
            }
            @if (isLocked(item)) {
              <span nz-icon nzType="lock" class="menu-item-lock"></span>
            }
          </li>
        }
      }
    </ul>
  </div>

  <!-- User Account Footer -->
  <div class="sidebar-footer" [class.collapsed]="isCollapsed">
    <div class="user-profile" [class.open]="userMenuOpen()">
      @if (isCollapsed) {
        <button
          type="button"
          class="user-avatar"
          (click)="toggleUserMenu($event)"
          [attr.aria-label]="'profile.menu' | translate"
          [title]="'profile.menu' | translate"
          id="sidebar-user-avatar"
        >
          <img [src]="userPhoto || 'assets/image.png'" [alt]="userFirstName + ' ' + userLastName" />
          @if (notifUnreadCount() > 0) {
            <span class="user-avatar__badge">{{
              notifUnreadCount() > 99 ? '99+' : notifUnreadCount()
            }}</span>
          }
        </button>
      } @else {
        <div class="user-avatar" id="sidebar-user-avatar">
          <img [src]="userPhoto || 'assets/image.png'" [alt]="userFirstName + ' ' + userLastName" />
        </div>

        <button
          type="button"
          class="user-info-button"
          (click)="toggleUserMenu($event)"
          id="sidebar-user-info-button"
        >
          <span class="user-name">{{ userFirstName }} {{ userLastName }}</span>
          <span class="user-chevron">
            <span nz-icon nzType="up"></span>
          </span>
        </button>
        <app-notification-bell [isCollapsed]="isCollapsed"></app-notification-bell>
      }

      @if (userMenuOpen()) {
        <div class="user-menu" (click)="$event.stopPropagation()">
          <div class="user-menu__header">
            <div class="user-menu__name">{{ userFirstName }} {{ userLastName }}</div>
            @if (user()?.email) {
              <div class="user-menu__email">{{ user()?.email }}</div>
            }
          </div>

          @if (isCollapsed) {
            <button
              type="button"
              class="user-menu__item"
              (click)="openNotifications()"
              id="sidebar-user-menu-notifications-button"
            >
              <span nz-icon nzType="bell"></span>
              <span>{{ 'notifications.title' | translate }}</span>
              @if (notifUnreadCount() > 0) {
                <span class="user-menu__badge">{{
                  notifUnreadCount() > 99 ? '99+' : notifUnreadCount()
                }}</span>
              }
            </button>
          }

          <button
            type="button"
            class="user-menu__item"
            (click)="openProfileEdit()"
            id="sidebar-user-menu-edit-profile-button"
          >
            <span nz-icon nzType="edit"></span>
            <span>{{ 'profile.editTitle' | translate }}</span>
          </button>

          <div class="user-menu__divider"></div>

          <div class="user-menu__section-label">{{ 'profile.language' | translate }}</div>
          @for (l of availableLanguages; track l.code) {
            <button
              type="button"
              class="user-menu__item user-menu__lang"
              [class.active]="l.code === currentLanguage()"
              (click)="setLanguage(l.code)"
              [attr.id]="'sidebar-user-menu-lang-' + l.code"
            >
              <span class="user-menu__lang-flag">{{ l.flag }}</span>
              <span>{{ l.label }}</span>
              @if (l.code === currentLanguage()) {
                <span class="user-menu__check" nz-icon nzType="check"></span>
              }
            </button>
          }

          @if (pwaInstall.canInstall()) {
            <div class="user-menu__divider"></div>

            <button
              type="button"
              class="user-menu__item"
              (click)="installApp()"
              id="sidebar-user-menu-install-button"
            >
              <span nz-icon nzType="download"></span>
              <span>{{ 'pwa.install' | translate }}</span>
            </button>
          }

          <div class="user-menu__divider"></div>

          <button
            type="button"
            class="user-menu__item user-menu__logout"
            (click)="logout()"
            id="sidebar-user-menu-logout-button"
          >
            <span nz-icon nzType="logout"></span>
            <span>{{ 'profile.logout' | translate }}</span>
          </button>
        </div>
      }
    </div>
  </div>
</div>
